import { Injectable } from '@angular/core';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  type RegistrationResponseJSON,
  type AuthenticationResponseJSON
} from '@simplewebauthn/browser';
import { WebAuthnAbortService } from '@simplewebauthn/browser';
import { LoggerService } from './logger.service';
import { PasskeyFailureCode, PasskeyOperationError, PasskeyPhase } from '../models/passkey.model';

/**
 * Servicio para interactuar con la API WebAuthn del navegador.
 * 
 * Este servicio es una capa de abstracción sobre @simplewebauthn/browser
 * que maneja la creación y autenticación de Passkeys usando los APIs
 * nativos del navegador (Face ID, Touch ID, Windows Hello, etc.).
 * 
 * Responsabilidades:
 * - Verificar soporte de WebAuthn en el navegador
 * - Detectar disponibilidad de autenticadores biométricos
 * - Crear nuevas credenciales (registro)
 * - Autenticar con credenciales existentes (login/revalidación)
 * - Manejo de errores específicos de WebAuthn
 * 
 * @example
 * // Verificar soporte
 * if (this.webAuthnService.isWebAuthnSupported()) {
 *   const hasBiometric = await this.webAuthnService.hasPlatformAuthenticator();
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class WebAuthnService {

  constructor(private logger: LoggerService) {}

  /**
   * Verifica si el navegador soporta WebAuthn.
   * 
   * WebAuthn es soportado por:
   * - Chrome 67+
   * - Firefox 60+
   * - Safari 13+
   * - Edge 18+
   * 
   * @returns true si el navegador soporta WebAuthn
   */
  isWebAuthnSupported(): boolean {
    const supported = browserSupportsWebAuthn();
    this.logger.info('🔍 [WebAuthn] Soporte del navegador:', supported);
    return supported;
  }

  /**
   * Verifica si el dispositivo tiene autenticador de plataforma integrado.
   * 
   * Autenticadores de plataforma incluyen:
   * - Face ID / Touch ID (iOS/macOS)
   * - Windows Hello (Windows)
   * - Huella digital (Android)
   * 
   * @returns Promise que resuelve a true si hay autenticador disponible
   */
  async hasPlatformAuthenticator(): Promise<boolean> {
    try {
      const available = await platformAuthenticatorIsAvailable();
      this.logger.info('🔍 [WebAuthn] Autenticador de plataforma disponible:', available);
      return available;
    } catch (error) {
      this.logger.error('❌ [WebAuthn] Error verificando autenticador:', error);
      return false;
    }
  }

  /**
   * Inicia el proceso de registro de una nueva Passkey.
   * 
   * Este método invoca el API nativo del navegador para crear una nueva
   * credencial criptográfica. El usuario verá el prompt biométrico
   * (Face ID, Touch ID, etc.) para confirmar la creación.
   * 
   * Flujo:
   * 1. Recibe opciones del backend (challenge, user info, etc.)
   * 2. Invoca navigator.credentials.create()
   * 3. Usuario confirma con biométrico
   * 4. Retorna la credencial creada
   * 
   * @param options - Opciones de creación recibidas del backend
   * @returns Promise con la credencial registrada
   * @throws Error si el usuario cancela o hay un problema técnico
   */
  async registerPasskey(
    options: PublicKeyCredentialCreationOptionsJSON
  ): Promise<RegistrationResponseJSON> {
    this.logger.log('🔐 [WebAuthn] Iniciando registro de passkey...');
    this.logger.debug('   → Challenge:', options.challenge.substring(0, 10) + '...');
    this.logger.debug('   → User:', options.user.displayName);

    try {
      // startRegistration espera un objeto con la propiedad optionsJSON
      const credential = await startRegistration({ optionsJSON: options });
      
      this.logger.log('✅ [WebAuthn] Registro exitoso');
      this.logger.debug('   → Credential ID:', credential.id.substring(0, 20) + '...');
      
      return credential;
    } catch (error: any) {
      this.logger.error('❌ [WebAuthn] Error en registro:', error?.name || 'UnknownError');
      throw this.normalizeError(error, 'authenticator');
    }
  }

  /**
   * Inicia el proceso de autenticación con una Passkey existente.
   * 
   * Este método invoca el API nativo del navegador para obtener una firma
   * criptográfica que prueba que el usuario posee la clave privada.
   * 
   * Flujo:
   * 1. Recibe opciones del backend (challenge, credenciales permitidas)
   * 2. Invoca navigator.credentials.get()
   * 3. Usuario confirma con biométrico
   * 4. Retorna la firma criptográfica
   * 
   * Se usa tanto para:
   * - Login inicial
   * - Re-validación periódica
   * 
   * @param options - Opciones de autenticación recibidas del backend
   * @returns Promise con la firma de autenticación
   * @throws Error si el usuario cancela o hay un problema técnico
   */
  async authenticateWithPasskey(
    options: PublicKeyCredentialRequestOptionsJSON,
    phase: PasskeyPhase = 'authenticator'
  ): Promise<AuthenticationResponseJSON> {
    this.logger.log('🔐 [WebAuthn] Iniciando autenticación con passkey...');

    try {
      // startAuthentication espera un objeto con la propiedad optionsJSON
      const assertion = await startAuthentication({ optionsJSON: options });
      
      this.logger.log('✅ [WebAuthn] Autenticación exitosa');
      
      return assertion;
    } catch (error: any) {
      this.logger.error('❌ [WebAuthn] Error en autenticación:', error?.name || 'UnknownError');
      throw this.normalizeError(error, phase);
    }
  }

  cancelAuthentication(): void {
    WebAuthnAbortService.cancelCeremony();
  }

  /**
   * Obtiene el nombre del método biométrico disponible en el dispositivo.
   * 
   * Útil para mostrar mensajes personalizados al usuario como:
   * "Usa Face ID para continuar" en lugar de "Usa tu passkey"
   * 
   * Detección basada en User Agent y disponibilidad de autenticador:
   * - iOS: Face ID o Touch ID
   * - macOS: Touch ID o contraseña de Mac
   * - Android: Huella Digital o PIN
   * - Windows: Windows Hello (PIN, huella, facial o contraseña)
   * - Sin biometría: PIN o contraseña del dispositivo
   * 
   * @returns Promise con el nombre del método biométrico
   */
  async getBiometricMethodName(): Promise<string> {
    const userAgent = navigator.userAgent.toLowerCase();
    const hasAuthenticator = await this.hasPlatformAuthenticator();
    
    // iOS (iPhone/iPad)
    if (/iphone|ipad|ipod/.test(userAgent)) {
      if (hasAuthenticator) {
        // Nota: No hay forma directa de detectar Face ID vs Touch ID
        // Asumimos Face ID en modelos recientes (iPhone X+)
        return 'Face ID o Touch ID';
      }
      return 'PIN del dispositivo';
    }
    
    // macOS
    if (/macintosh|mac os x/.test(userAgent)) {
      if (hasAuthenticator) {
        return 'Touch ID o contraseña de Mac';
      }
      return 'contraseña de Mac';
    }
    
    // Android
    if (/android/.test(userAgent)) {
      if (hasAuthenticator) {
        return 'huella digital o PIN';
      }
      return 'PIN del dispositivo';
    }
    
    // Windows
    if (/windows/.test(userAgent)) {
      if (hasAuthenticator) {
        return 'Windows Hello (PIN, huella, facial o contraseña)';
      }
      return 'PIN o contraseña de Windows';
    }
    
    // Genérico (fallback)
    if (hasAuthenticator) {
      return 'biometría o PIN del dispositivo';
    }
    return 'PIN o contraseña del dispositivo';
  }

  /**
   * Verifica compatibilidad COMPLETA de WebAuthn.
   * 
   * Realiza verificaciones exhaustivas:
   * 1. API WebAuthn disponible
   * 2. Autenticador de plataforma disponible
   * 3. Navegador soportado (versión mínima)
   * 4. Sistema operativo soportado
   * 5. Versión de iOS >= 16 (si aplica)
   * 
   * Si alguna verificación falla, retorna false y el sistema
   * debe usar PIN por email como fallback.
   * 
   * @returns true si el dispositivo es completamente compatible
   */
  async isFullyCompatible(): Promise<boolean> {
    this.logger.log('🔍 [WebAuthn] Verificando compatibilidad completa...');

    // 1. Verificar API básica
    if (!this.isWebAuthnSupported()) {
      this.logger.warn('⚠️ [WebAuthn] API WebAuthn no disponible');
      return false;
    }

    // 2. Verificar autenticador de plataforma
    const hasAuthenticator = await this.hasPlatformAuthenticator();
    if (!hasAuthenticator) {
      this.logger.warn('⚠️ [WebAuthn] Autenticador de plataforma no disponible');
      return false;
    }

    // 3. Obtener información del navegador
    const browserInfo = this.getBrowserInfo();
    this.logger.debug('   → Navegador:', browserInfo.name, browserInfo.version);
    this.logger.debug('   → OS:', browserInfo.os, browserInfo.osVersion);

    // 4. Verificar navegador soportado
    if (!this.isBrowserSupported(browserInfo)) {
      this.logger.warn('⚠️ [WebAuthn] Navegador no soportado:', browserInfo.name, browserInfo.version);
      return false;
    }

    // 5. Verificar versión de iOS si aplica
    if (browserInfo.os === 'iOS') {
      const iosVersion = this.getIOSVersion();
      if (iosVersion < 16) {
        this.logger.warn('⚠️ [WebAuthn] iOS version no soportada:', iosVersion);
        return false;
      }
    }

    this.logger.log('✅ [WebAuthn] Dispositivo completamente compatible');
    return true;
  }

  /**
   * Obtiene información detallada del navegador y sistema operativo.
   * 
   * @returns Objeto con información del navegador
   * @private
   */
  private getBrowserInfo(): BrowserInfo {
    const ua = navigator.userAgent;
    
    return {
      name: this.detectBrowserName(ua),
      version: this.detectBrowserVersion(ua),
      os: this.detectOS(ua),
      osVersion: this.detectOSVersion(ua)
    };
  }

  /**
   * Detecta el nombre del navegador.
   * 
   * @param ua - User Agent string
   * @returns Nombre del navegador
   * @private
   */
  private detectBrowserName(ua: string): string {
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome/')) return 'Chrome';
    if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('OPR/') || ua.includes('Opera/')) return 'Opera';
    return 'Unknown';
  }

  /**
   * Detecta la versión del navegador.
   * 
   * @param ua - User Agent string
   * @returns Versión del navegador
   * @private
   */
  private detectBrowserVersion(ua: string): number {
    let match: RegExpMatchArray | null = null;
    
    if (ua.includes('Edg/')) {
      match = ua.match(/Edg\/(\d+)/);
    } else if (ua.includes('Chrome/')) {
      match = ua.match(/Chrome\/(\d+)/);
    } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
      match = ua.match(/Version\/(\d+)/);
    } else if (ua.includes('Firefox/')) {
      match = ua.match(/Firefox\/(\d+)/);
    } else if (ua.includes('OPR/')) {
      match = ua.match(/OPR\/(\d+)/);
    }
    
    return match ? parseInt(match[1]) : 0;
  }

  /**
   * Detecta el sistema operativo.
   * 
   * @param ua - User Agent string
   * @returns Nombre del sistema operativo
   * @private
   */
  private detectOS(ua: string): string {
    if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) return 'iOS';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('Mac OS X') || ua.includes('Macintosh')) return 'macOS';
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Linux')) return 'Linux';
    return 'Unknown';
  }

  /**
   * Detecta la versión del sistema operativo.
   * 
   * @param ua - User Agent string
   * @returns Versión del sistema operativo
   * @private
   */
  private detectOSVersion(ua: string): string {
    let match: RegExpMatchArray | null = null;
    
    if (ua.includes('iPhone') || ua.includes('iPad')) {
      match = ua.match(/OS (\d+)_(\d+)/);
      return match ? `${match[1]}.${match[2]}` : 'Unknown';
    }
    
    if (ua.includes('Android')) {
      match = ua.match(/Android (\d+\.?\d*)/);
      return match ? match[1] : 'Unknown';
    }
    
    if (ua.includes('Mac OS X')) {
      match = ua.match(/Mac OS X (\d+[._]\d+)/);
      return match ? match[1].replace('_', '.') : 'Unknown';
    }
    
    if (ua.includes('Windows NT')) {
      match = ua.match(/Windows NT (\d+\.\d+)/);
      return match ? match[1] : 'Unknown';
    }
    
    return 'Unknown';
  }

  /**
   * Obtiene la versión de iOS como número entero.
   * 
   * @returns Versión de iOS (ej: 16, 15, 14) o 0 si no es iOS
   * @private
   */
  private getIOSVersion(): number {
    const ua = navigator.userAgent;
    const match = ua.match(/OS (\d+)_/);
    return match ? parseInt(match[1]) : 0;
  }

  /**
   * Verifica si el navegador está soportado según versiones mínimas.
   * 
   * Versiones mínimas requeridas:
   * - Chrome: 67+
   * - Safari: 13+
   * - Firefox: 60+
   * - Edge: 18+
   * - Opera: 54+
   * 
   * @param info - Información del navegador
   * @returns true si el navegador está soportado
   * @private
   */
  private isBrowserSupported(info: BrowserInfo): boolean {
    const minVersions: { [key: string]: number } = {
      'Chrome': 67,
      'Safari': 13,
      'Firefox': 60,
      'Edge': 18,
      'Opera': 54
    };

    const minVersion = minVersions[info.name];
    if (!minVersion) {
      this.logger.warn('⚠️ [WebAuthn] Navegador desconocido:', info.name);
      return false;
    }

    return info.version >= minVersion;
  }

  /**
   * Obtiene un mensaje de incompatibilidad específico según el problema detectado.
   * 
   * Proporciona mensajes claros y accionables para el usuario:
   * - Navegador antiguo → Sugerir actualización
   * - iOS < 16 → Indicar versión requerida
   * - Sin biométrico → Explicar requisito
   * - Genérico → Fallback a PIN
   * 
   * @returns Mensaje de incompatibilidad user-friendly
   */
  async getIncompatibilityMessage(): Promise<string> {
    const browserInfo = this.getBrowserInfo();
    
    // Navegador no soportado
    if (!this.isBrowserSupported(browserInfo)) {
      const minVersions: { [key: string]: number } = {
        'Chrome': 67,
        'Safari': 13,
        'Firefox': 60,
        'Edge': 18,
        'Opera': 54
      };
      
      const minVersion = minVersions[browserInfo.name];
      
      if (minVersion) {
        return `Tu navegador ${browserInfo.name} ${browserInfo.version} no soporta Passkeys. Se requiere ${browserInfo.name} ${minVersion} o superior. Actualiza tu navegador o usa Chrome, Safari, Edge o Firefox.`;
      }
      
      return `Tu navegador no soporta Passkeys. Usa Chrome, Safari, Edge o Firefox actualizado.`;
    }
    
    // iOS antiguo
    if (browserInfo.os === 'iOS') {
      const iosVersion = this.getIOSVersion();
      if (iosVersion < 16) {
        return `Las Passkeys requieren iOS 16 o superior. Actualmente tienes iOS ${iosVersion}. Actualiza tu dispositivo o usa el PIN por email.`;
      }
    }
    
    // Sin autenticador
    const hasAuthenticator = await this.hasPlatformAuthenticator();
    if (!hasAuthenticator) {
      return 'Tu dispositivo no tiene autenticación biométrica configurada. Configura Face ID, Touch ID o Windows Hello, o usa el PIN por email.';
    }
    
    // Mensaje genérico
    return 'Tu dispositivo no soporta Passkeys. Usa el PIN por email para validar tu identidad.';
  }

  private normalizeError(error: any, phase: PasskeyPhase): PasskeyOperationError {
    const originalName = error?.name || '';
    const codes: Record<string, PasskeyFailureCode> = {
      AbortError: 'CANCELLED', TimeoutError: 'TIMEOUT', NotAllowedError: 'NOT_ALLOWED',
      NotSupportedError: 'UNSUPPORTED', SecurityError: 'SECURITY'
    };
    return new PasskeyOperationError(codes[originalName] ?? 'UNKNOWN', phase, originalName,
      error?.status, { cause: error });
  }
}

/**
 * Información del navegador y sistema operativo
 */
interface BrowserInfo {
  name: string;
  version: number;
  os: string;
  osVersion: string;
}
